import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './InlinePickerSelect.css';

const LIST_MAX = 440;
const LIST_MIN = 160;
const LIST_GAP = 12;

// Phones and tablets: opening the picker should show the list, not throw the
// keyboard over it. The search box is still one tap away.
const isTouch = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

const scrollParent = (el) => {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const oy = window.getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll') return p;
  }
  return null;
};

// Lowest point the list can show on screen: the top of the on-screen keyboard
// (visualViewport shrinks with it) or the bottom of the scrolling box it sits
// in (a panel body ends above its Cancel/Save footer), whichever is higher.
const visibleBottom = (el) => {
  const vv = window.visualViewport;
  const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  const box = scrollParent(el);
  return box ? Math.min(bottom, box.getBoundingClientRect().bottom) : bottom;
};

/**
 * InlinePickerSelect - Searchable dropdown with "Create new" option.
 * Matches the client picker UI pattern for consistency.
 *
 * Props:
 *   options       - [{ value, label }]
 *   value         - { value, label } or null
 *   onChange      - (selected) => void
 *   onCreateNew   - (name) => Promise (called when creating new item, should return the new option)
 *   placeholder   - string
 *   createLabel   - string (e.g. "Create new category")
 *   searchPlaceholder - string
 *   label         - string (field label above the picker)
 *   error         - string (error message)
 */
const InlinePickerSelect = ({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = 'Select...',
  createLabel = 'Create new',
  searchPlaceholder = 'Search...',
  label,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listMax, setListMax] = useState(null);
  const ref = useRef(null);
  const listRef = useRef(null);

  // Fit the list to the part of the screen you can actually see. The CSS cap
  // (60vh) ignores the on-screen keyboard, which covers the page in the iOS
  // app and on Android, so the bottom of a long list sat behind it and could
  // never be scrolled into view. visualViewport shrinks with the keyboard.
  useLayoutEffect(() => {
    if (!open) { setListMax(null); return undefined; }
    const fit = () => {
      const el = listRef.current;
      if (!el) return;
      const room = visibleBottom(el) - el.getBoundingClientRect().top - LIST_GAP;
      setListMax(Math.round(Math.max(LIST_MIN, Math.min(LIST_MAX, room))));
    };
    fit();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', fit);
      vv.addEventListener('scroll', fit);
    }
    window.addEventListener('resize', fit);
    // The panel body scrolls (and SlidePanel scrolls a focused input into
    // view), which moves the list; capture catches any scrolling ancestor.
    document.addEventListener('scroll', fit, true);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', fit);
        vv.removeEventListener('scroll', fit);
      }
      window.removeEventListener('resize', fit);
      document.removeEventListener('scroll', fit, true);
    };
  }, [open, creating]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
    setSearch('');
    setCreating(false);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (onCreateNew) {
        await onCreateNew(name);
      }
      setCreating(false);
      setNewName('');
      setOpen(false);
    } catch { /* handled by parent */ }
    setIsSubmitting(false);
  };

  return (
    <div className="ips-wrap" ref={ref}>
      {label && <label className="ips-label">{label}</label>}
      <button
        type="button"
        className={`ips-trigger ${open ? 'ips-trigger--open' : ''} ${value ? 'ips-trigger--filled' : ''}`}
        onClick={() => { setOpen(!open); setCreating(false); setSearch(''); }}
      >
        <span>{value ? value.label : placeholder}</span>
        <svg className={`ips-chevron ${open ? 'ips-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="ips-dropdown">
          {/* Search */}
          <div className="ips-search">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8e8e93" strokeWidth="1.3">
              <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={creating ? '' : search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => {
                // Keyboard on its way: lift the picker to the top of the panel
                // so the list gets the room above the keyboard. Runs after
                // SlidePanel's own focus scroll (300ms) so this one wins.
                // Only the panel body scrolls: scrollIntoView would also
                // scroll the clipped sheet and slide the whole panel down.
                if (!isTouch()) return;
                setTimeout(() => {
                  const box = ref.current && scrollParent(ref.current);
                  if (!box) return;
                  const offset = ref.current.getBoundingClientRect().top - box.getBoundingClientRect().top - 8;
                  box.scrollTo({ top: box.scrollTop + offset, behavior: 'smooth' });
                }, 350);
              }}
              autoFocus={!isTouch()}
              disabled={creating}
            />
          </div>

          <div
            className="ips-list"
            ref={listRef}
            style={listMax ? { maxHeight: listMax } : undefined}
          >
            {/* Create new - at top */}
            {onCreateNew && !creating && (
              <button type="button" className="ips-item ips-item--create" onClick={() => { setCreating(true); setSearch(''); }}>
                <div className="ips-item-icon ips-item-icon--add">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="ips-item-info">
                  <span className="ips-item-name">{createLabel}</span>
                </div>
              </button>
            )}

            {/* Inline create form */}
            {creating && (
              <div className="ips-create-form">
                <input
                  type="text"
                  className="ips-create-input"
                  placeholder="Enter name..."
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                  autoFocus
                />
                <button type="button" className="ips-create-cancel" onClick={() => { setCreating(false); setNewName(''); }}>
                  Cancel
                </button>
                <button type="button" className="ips-create-btn" onClick={handleCreate} disabled={!newName.trim() || isSubmitting}>
                  {isSubmitting ? '...' : 'Add'}
                </button>
              </div>
            )}

            {/* Options */}
            {!creating && filtered.map(opt => {
              const isActive = value?.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`ips-item ${isActive ? 'ips-item--active' : ''}`}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="ips-item-name">{opt.label}</span>
                  {isActive && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7.5L5.5 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              );
            })}

            {!creating && filtered.length === 0 && (
              <div className="ips-empty">No results found</div>
            )}
          </div>
        </div>
      )}

      {error && <span className="cm_error_msg">{error}</span>}
    </div>
  );
};

export default InlinePickerSelect;
